import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { emberApi, type Knock } from '../api';
import { track } from '../../../../../shared-web/track';
import { KnockCard } from './KnockCard';
import { EmberMascot } from './EmberMascot';
import { LetterGlyph } from './LetterGlyph';
import { Modal } from './Modal';
import { isLetter, knockAbout, knockHeadline, letterId, letterOpened, letterState, markLetterOpened } from './knock-letters';
import './KnockLetter.css';

/**
 * A letter, opened (v4.0 §4.9, 2026-09-07 拍板; flat paper 2026-09-10). One
 * sheet of letter paper held up in front of whatever page the person is on —
 * the tree, the chat — over a veil that leaves the page visible. The paper
 * carries Ember's hand, the one sentence Ember has to say, the Ember it is
 * about, and the letter's body; answering turns the page into its settled
 * sentence (`You said yes.`) and the letter stays until it is put down, so
 * nothing vanishes under a thumb.
 *
 * Several letters lie one under the other; the pager at the foot turns
 * between them (2026-09-08: two arrows around `i of N`, no wrap — the arrow
 * on whichever side still holds an unread letter carries the red dot the top
 * bar's badge wears, so finishing the first letter points at the next). The
 * pile is fixed when it is picked up: a letter answered here is no longer a
 * letter, but it is still one of these pages.
 */
export function KnockLetter({ knocks, onChange, onClose, onOpened, initialExpanded = '' }: {
  knocks: Knock[];
  onChange: (next: Knock) => void;
  onClose: () => void;
  /** The letter on top as the pile is picked up (letterId) — the one a text linked to (§4.14 `/?letter=`). */
  initialExpanded?: string;
  /** A letter was shown — the envelope outside is opened now. */
  onOpened?: (id: string) => void;
}): ReactElement {
  const titleId = useId();
  // The pile, fixed as it is picked up. Pages are found again by the row
  // itself (role + match), not by letterId: a row stops being a letter
  // when it is answered here, and the page must stay under the thumb.
  const [pile] = useState(() => {
    const all = knocks.filter(isLetter).map((k) => ({ id: letterId(k), role: k.role, match_id: k.match_id }));
    const first = all.findIndex((p) => p.id === initialExpanded);
    return first > 0 ? [...all.slice(first), ...all.slice(0, first)] : all;
  });
  const [index, setIndex] = useState(0);
  // The pile's own copy of each page (2026-10-22 jam; the apps' LetterSheet
  // since 2026-10-02 Riley): an answer re-reads the list, and an answered
  // room invitation is no longer on it — the paper is still in hand, so it
  // draws from the copy kept here, in its settled sentence, until put down.
  const held = useRef(new Map<string, Knock>());
  const pages = pile
    .map((p) => {
      const key = `${p.role}:${p.match_id}`;
      const live = knocks.find((k) => k.role === p.role && k.match_id === p.match_id);
      if (live) held.current.set(key, live);
      return live ?? held.current.get(key);
    })
    .filter((k): k is Knock => Boolean(k));
  const page = pages[index];

  // Showing a letter IS the person reading it. A read's note letter has no
  // door but this: the card marks the other side's notes seen as it shows.
  useEffect(() => {
    if (!page) return;
    const id = letterId(page);
    if (!letterOpened(id)) {
      markLetterOpened(id);
      onOpened?.(id);
    }
    track('ui.clicked', { name: 'knock_letter_open', state: letterState(page), ...(knockEmberId(page)) });
    // The asker's answers shown is the asker's look (§4.19): the letter ends,
    // the answers stay on the page.
    if (page.letter === 'answers_arrived' && page.role === 'asker') {
      emberApi.markQuestionSeen(page.question.id)
        .then(() => onChange({ ...page, letter: null, answers: page.answers.map((a) => ({ ...a, seen: true })) }))
        .catch(() => { /* seen again next visit */ });
    }
    if (page.letter === 'answer_mattered' && page.role === 'answerer') {
      emberApi.markQuestionSeen(page.question.id)
        .then(() => onChange({ ...page, letter: null }))
        .catch(() => { /* seen again next visit */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  if (!page) return <></>;
  const about = knockAbout(page);
  // A page answered in this sitting is no longer a letter, and not unread.
  const unread = (ks: Knock[]): boolean => ks.some((k) => isLetter(k) && !letterOpened(letterId(k)));
  const unreadBefore = unread(pages.slice(0, index));
  const unreadAfter = unread(pages.slice(index + 1));
  return (
    <Modal onClose={onClose} labelledBy={titleId} className="letter-modal">
      <div className="letter">
        <div className="letter-sheet">
          <button type="button" className="letter-close" aria-label="Put it down" data-log="door_close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
          <div className="letter-hand"><EmberMascot face="comforting" interactive={false} className="letter-mascot" /></div>
          <h2 id={titleId} className="letter-head">{knockHeadline(page)}</h2>
          {about ? <p className="letter-about">About “{about}”.</p> : null}
          {page.role === 'answerer' ? <p className="letter-about">From someone talking with Ember. They won’t know who you are.</p> : null}
          {page.role === 'read_reader' ? <p className="letter-about">Someone else’s Ember. They won’t know who you are.</p> : null}
          <KnockCard key={`${page.role}:${page.match_id}`} knock={page} onChange={onChange} frame="letter" />
          <p className="letter-sign">Ember</p>
          {pages.length > 1 ? (
            <div className="letter-foot">
              <button
                type="button"
                className="letter-turn"
                aria-label="Previous letter"
                data-log="knock_letter_next"
                data-log-state="prev"
                disabled={index === 0}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m15 6-6 6 6 6" />
                </svg>
                {unreadBefore ? <span className="letter-turn-dot" aria-label="Unread letters" /> : null}
              </button>
              <span className="letter-page">{index + 1} of {pages.length}</span>
              <button
                type="button"
                className="letter-turn"
                aria-label="Next letter"
                data-log="knock_letter_next"
                data-log-state="next"
                disabled={index === pages.length - 1}
                onClick={() => setIndex((i) => Math.min(pages.length - 1, i + 1))}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="m9 6 6 6-6 6" />
                </svg>
                {unreadAfter ? <span className="letter-turn-dot" aria-label="Unread letters" /> : null}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/**
 * The door line (§4.5, §4.12): one line per letter — on the chat, under the
 * header in the strip where the telling's own question also stands; on a
 * notebook card (2026-09-08), under the Ember the letter is about. It names
 * what Ember has to say and which Ember it is about; a tap opens the paper on
 * that letter. `marked` puts the top bar's red dot on each letter the Embers
 * tab counts: the tab's number and the dots on the cards are the same fact,
 * one to one.
 */
export function KnockDoor({ letters, onOpen, marked = false, className = '' }: {
  letters: Knock[];
  onOpen: (letter: Knock) => void;
  marked?: boolean;
  className?: string;
}): ReactElement | null {
  if (letters.length === 0) return null;
  return (
    <div className={`letter-door${className ? ` ${className}` : ''}`} data-log-seen="knock_door">
      {letters.map((k) => {
        const id = letterId(k);
        const about = knockAbout(k);
        return (
          <button
            key={id}
            type="button"
            className="letter-door-line"
            data-log="knock_door"
            data-log-state={letterState(k)}
            data-log-match-id={k.match_id}
            onClick={() => onOpen(k)}
          >
            <span className="letter-door-glyph-wrap">
              <LetterGlyph sealed={!letterOpened(id)} size={26} className="letter-door-glyph" />
              {marked && isLetter(k) ? <span className="letter-door-dot" aria-hidden /> : null}
            </span>
            <span className="letter-door-text">
              <span className="letter-door-head">{knockHeadline(k)}</span>
              {about ? <> About “{about}”.</> : null}
            </span>
            <svg className="letter-door-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m9 6 6 6-6 6" />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

/** The letter's keys for the event: the Ember it is about, and the row itself (match_id, 2026-09-18). */
function knockEmberId(k: Knock): { ember_id?: string; match_id: string } {
  const id = k.role === 'share' || k.role === 'read_owner' || k.role === 'read_reader' ? k.ember.id : undefined;
  return { ...(id ? { ember_id: id } : {}), match_id: k.match_id };
}
